import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { partition, filter, sortBy, isEqual, sortedUniqBy } from 'lodash';

import LoadingSpinner from 'components/ui/loading_spinner';
import CsvDownloadButton from 'components/csv_download_button';
import SelectFilter from 'components/search_sidebar/select_filter';
import SearchPage from 'components/search_page';
import FilterTopbar from 'components/search_page/filter_topbar';
import FilterRow from 'components/search_page/filter_row';
import taskPropTypes from 'components/tasks/prop_types';
import GenericTaskList from 'components/tasks/task_list';
import PageHeader from 'components/project/page_header';
import {
  addTask,
  removeTask,
  toggleTaskCompleted,
  updateTask,
  showEditNoteModal,
  showNewNoteModal,
  showEditTaskModal,
  showNewTaskModal
} from 'bundles/project/actions/index';
import { Button } from 'react-bootstrap-5';
import { faListCheck } from '@fortawesome/pro-solid-svg-icons';

const filterOptions = {
  status: [
    {
      value: '',
      label: 'All Statuses'
    },
    {
      value: 'completed',
      label: 'Completed'
    },
    {
      value: 'open',
      label: 'Open'
    }
  ],
  type: [
    {
      value: '',
      label: 'All Types'
    },
    {
      value: 'call',
      label: 'Call'
    },
    {
      value: 'email',
      label: 'Email'
    },
    {
      value: 'meeting',
      label: 'Meeting'
    },
    {
      value: 'other',
      label: 'Other'
    },
    {
      value: 'showing',
      label: 'Showing'
    },
    {
      value: 'critical_date',
      label: 'Critical Date'
    }
  ]
};

const resultsPerPage = 20;

export class TaskList extends React.Component {
  static propTypes = {
    addTask: PropTypes.func.isRequired,
    availableDatabaseOptions: PropTypes.arrayOf(PropTypes.array).isRequired,
    databaseMembers: PropTypes.array,
    defaultAssociatedModel: PropTypes.shape({
      associatedModelId: PropTypes.number.isRequired,
      associatedModelType: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      default: PropTypes.bool.isRequired
    }).isRequired,
    fetching: PropTypes.bool.isRequired,
    hideContactSelect: PropTypes.bool,
    hidePropertySelect: PropTypes.bool,
    projectId: PropTypes.number.isRequired,
    removeTask: PropTypes.func.isRequired,
    showEditTaskModal: PropTypes.func.isRequired,
    showNewTaskModal: PropTypes.func.isRequired,
    tasks: PropTypes.arrayOf(taskPropTypes),
    tasksCsvPath: PropTypes.string,
    toggleTaskCompleted: PropTypes.func.isRequired,
    updateTask: PropTypes.func.isRequired
  };

  state = {
    limit: resultsPerPage,
    filters: {
      status: null,
      type: null,
      user: null,
      contact: null
    }
  }

  getContactOptions = () => {
    const options = [];

    this.props.tasks.forEach((task) => {
      const contactIds = options.map(o => o.value.id);
      const contacts = [];

      task.associatedModels.forEach((model) => {
        if (model.associatedModelType.includes("Contact") && !contactIds.includes(model.associatedModelId)) {
          contacts.push(model);
        }
      });

      const mappedOptions = contacts.map(contact => ({
        value: {
          id: contact.associatedModelId,
          type: contact.associatedModelType
        },
        label: contact.name
      }));

      options.push(...mappedOptions);
    });

    return sortBy(options, o => o.label.toUpperCase());
  }

  filterOptions() {
    const users = this.props.tasks.map(c => ({ value: c.assigneeId, label: c.assignee }));

    const filters = {
      ...filterOptions,
      user: [
        { value: '', label: 'All Users' },
        ...sortedUniqBy(sortBy(users, 'value'), 'value')
      ]
    };

    const contacts = this.getContactOptions();

    if (contacts.length > 0) filters.contact = [{ value: '', label: 'All Contacts' }, ...contacts];

    return filters;
  }

  statusFilter = (tasks) => {
    if (!this.state.filters.status) return tasks;
    const [pastTasks, openTasks] = partition(tasks, t => t.completedAt);
    if (this.state.filters.status === 'completed') {
      return pastTasks;
    }
    if (this.state.filters.status === 'open') {
      return openTasks;
    }
  }

  typeFilter = (tasks) => {
    if (!this.state.filters.type) return tasks;

    return filter(tasks, task => this.state.filters.type === task.taskType);
  }

  userFilter = (tasks) => {
    if (!this.state.filters.user) return tasks;

    return filter(tasks, task => this.state.filters.user === task.assigneeId);
  }

  contactFilter = (tasks) => {
    if (!this.state.filters.contact) return tasks;

    return filter(tasks, (task) => {
      const associatedModels = task.associatedModels.map(model => ({
        id: model.associatedModelId,
        type: model.associatedModelType
      }));

      return !!associatedModels.find(model => isEqual(model, this.state.filters.contact));
    });
  }

  filteredTasks() {
    let tasks = this.props.tasks;
    tasks = this.statusFilter(tasks);
    tasks = this.typeFilter(tasks);
    tasks = this.userFilter(tasks);
    tasks = this.contactFilter(tasks);

    return tasks || [];
  }

  handleTaskStateChange = (task) => {
    this.props.toggleTaskCompleted(task);
  }

  currentFilterValue = (filters, key) => filters[key].find(option => option.value === this.state.filters[key])

  updateFilter = (e, key) => {
    this.setState((prevState) => {
      const filters = { ...prevState.filters };
      filters[key] = e ? e.value : null;
      return { filters };
    });
  }

  orderedTasks = () => {
    let [completedTasks, openTasks] = partition(this.filteredTasks(), t => t.completedAt);

    openTasks = sortBy(openTasks, x => x.dueDate);
    completedTasks = sortBy(completedTasks, x => x.completedAt).reverse();

    return { completedTasks, openTasks };
  }

  allOrderedTasks = () => {
    const { completedTasks, openTasks } = this.orderedTasks();
    return openTasks.concat(completedTasks);
  }

  incrementDisplayLimit = () => {
    this.setState((state) => {
      const limit = state.limit + resultsPerPage;
      return { limit };
    });
  }

  tasksForCurrentLimit = () => {
    const tasks = this.allOrderedTasks().slice(0, this.state.limit);
    const [completedTasks, openTasks] = partition(tasks, t => t.completedAt);
    return [...openTasks, ...completedTasks];
  }

  render() {
    const headerActions = (
      <PageHeader.Actions>
        <div className="d-flex align-items-center gap-4">
          {!this.props.fetching && <CsvDownloadButton entityName="tasks" href={this.props.tasksCsvPath} />}
          {this.renderCTA()}
        </div>
      </PageHeader.Actions>
    );

    return (
      <React.Fragment>
        <PageHeader actions={headerActions} title="Tasks" />
        {this.renderFilterTopbar()}
        {this.renderTaskTable()}
      </React.Fragment>
    );
  }

  renderCTA() {
    if (this.props.fetching) return;

    return (
      <Button name="createTaskCTA" onClick={this.props.showNewTaskModal}>
        New Task
      </Button>
    );
  }

  renderFilterTopbar() {
    if (this.props.fetching) return;

    const pageData = {
      currentPage: 1,
      itemCount: this.tasksForCurrentLimit().length,
      itemSingularLabel: 'task',
      pageSize: this.state.limit,
      totalCount: this.filteredTasks().length
    };
    const filters = this.filterOptions();

    return (
      <SearchPage>
        <FilterTopbar className="w-100 p-0 border-0 mb-3">
          <FilterRow pageData={pageData}>
            {filters.contact && (
              <SelectFilter
                defaultValue={filters.contact[0]}
                name="contact-filter"
                options={filters.contact}
                value={this.currentFilterValue(filters, 'contact')}
                onChange={e => this.updateFilter(e, 'contact')}
              />
            )}
            {filters.user && (
              <div className={filters.contact && 'left-buffer-md'}>
                <SelectFilter
                  defaultValue={filters.user[0]}
                  name="name-filter"
                  options={filters.user}
                  value={this.currentFilterValue(filters, 'user')}
                  onChange={e => this.updateFilter(e, 'user')}
                />
              </div>
            )}
            <div className={(filters.contact || filters.user) && 'left-buffer-md'}>
              <SelectFilter
                defaultValue={filters.type[0]}
                name="type-filter"
                options={filters.type}
                value={this.currentFilterValue(filters, 'type')}
                onChange={e => this.updateFilter(e, 'type')}
              />
            </div>
            <div className="left-buffer-md">
              <SelectFilter
                defaultValue={filters.status[0]}
                name="status-filter"
                options={filters.status}
                value={this.currentFilterValue(filters, 'status')}
                onChange={e => this.updateFilter(e, 'status')}
              />
            </div>
          </FilterRow>
        </FilterTopbar>
      </SearchPage>
    );
  }

  renderTasks() {
    const tasks = this.tasksForCurrentLimit();

    return (
      <table className='w-100'>
        <tbody>
          <GenericTaskList
            availableDatabases={this.props.availableDatabaseOptions}
            databaseMembers={this.props.databaseMembers}
            defaultAssociatedModels={[this.props.defaultAssociatedModel]}
            hideActions
            hideContactSelect={this.props.hideContactSelect}
            hidePropertySelect={this.props.hidePropertySelect}
            multipleProjectsInTask={true}
            name="projectsTaskList"
            projectId={this.props.projectId}
            showLoadMoreLink={this.state.limit < this.filteredTasks().length}
            tasks={tasks}
            onDeleteTask={this.props.removeTask}
            onLoadMoreTasks={this.incrementDisplayLimit}
            onSaveTask={this.props.addTask}
            onToggleTaskState={this.props.toggleTaskCompleted}
            onUpdateTask={this.props.updateTask}
          />
        </tbody>
      </table>
    );
  }

  renderTaskTable() {
    if (this.props.fetching) {
      return (
        <div className="centered-image">
          <LoadingSpinner />
        </div>
      );
    }

    if (this.filteredTasks().length > 0) return <div name="taskTable">{this.renderTasks()}</div>;

    const noTasksOnProject = this.props.tasks.length === 0;

    return (
      <Empty className="bg-white">
        <Empty.Media>
          <FontAwesomeIcon icon={faListCheck} />
        </Empty.Media>
        <Empty.Title>
          {noTasksOnProject ? 'No Tasks to display.' : 'No Tasks match the current search criteria.'}
        </Empty.Title>
        <Empty.Content>
          {noTasksOnProject && <div>Get started by clicking on the “New Task” button.</div>}
        </Empty.Content>
      </Empty>
    );
  }
}

const mapStateToProps = ({ availableDatabaseOptions, project, taskOptions, tasks }) => {
  taskOptions = taskOptions || {};

  return {
    availableDatabaseOptions,
    defaultAssociatedModel: project.defaultTaskAssociation,
    databaseMembers: [...project.collaborators, ...project.otherAuthorizedUsers],
    fetching: !!tasks.fetching,
    hideContactSelect: taskOptions.hideContactSelect,
    hidePropertySelect: taskOptions.hidePropertySelect,
    projectId: project.id,
    tasks: tasks.fetching ? [] : tasks.filter(task => task.taskType !== 'note')
  };
};

const actions = {
  addTask,
  updateTask,
  toggleTaskCompleted,
  removeTask,
  showEditNoteModal,
  showNewNoteModal,
  showEditTaskModal,
  showNewTaskModal
};

export default connect(mapStateToProps, actions)(TaskList);
